/**
 * Main Authentication Client
 *
 * Combines all modules into a unified client interface
 */

import {
  createHttpClient,
  setMiniProgramRuntime,
  type ClientResult,
  type HttpClient,
} from "@amaster.ai/http-client";
import { EventEmitter } from "./event-emitter";
import { createStorageAdapter, STORAGE_KEYS, type StorageAdapter } from "./storage";
import { TokenManager } from "./token-manager";
import { defaultHttpClientOptions } from "./http-config";
import { createAuthModule } from "./modules/auth";
import { createPermissionsModule } from "./modules/permissions";
import { createUserModule } from "./modules/user";
import { createOAuthModule } from "./modules/oauth";
import { createSessionsModule } from "./modules/sessions";
import type { AuthClientOptions, AuthEvent, EventHandler, User } from "./types";

type AuthInvalidationReason = "unauthorized" | "tokenExpired";

export type AuthClient = ReturnType<typeof createAuthModule> &
  ReturnType<typeof createPermissionsModule> &
  ReturnType<typeof createUserModule> &
  ReturnType<typeof createOAuthModule> &
  ReturnType<typeof createSessionsModule> & {
    // Events
    on(event: AuthEvent, handler: EventHandler): void;
    off(event: AuthEvent, handler: EventHandler): void;

    // Utilities
    isAuthenticated(): boolean;
    getAccessToken(): string | null;
    setAccessToken(token: string): void;
    clearAuth(): void;
  };

export function createAuthClient(
  options: AuthClientOptions = {},
  http?: HttpClient
): AuthClient {
  const {
    baseURL,
    headers,
    onTokenExpired,
    onUnauthorized,
    autoHandleOAuthCallback = true,
    autoRedirectAfterLogin = true,
    runtime,
  } = options;

  if (runtime) {
    setMiniProgramRuntime(runtime);
  }
  
  // Create HTTP client with user options if not provided
  const httpClient = http || createHttpClient({
    ...defaultHttpClientOptions,
    baseURL,
    headers,
    runtime,
  });

  const autoRefresh = true;
  const refreshThreshold = 300;

  // Auto-detect environment and use appropriate storage
  const storage: StorageAdapter = createStorageAdapter();
  const eventEmitter = new EventEmitter();
  const tokenManager = new TokenManager();

  let currentUser: User | null = null;
  let authInvalidated = false;

  // Load user from storage
  try {
    const userJson = storage.getItem(STORAGE_KEYS.USER);
    if (userJson) {
      currentUser = JSON.parse(userJson);
    }
  } catch (error) {
    console.error("[AuthClient] Failed to load user from storage:", error);
  }

  // Forward declare for circular dependency
  let authClient: AuthClient;

  function invalidateAuth(
    reason: AuthInvalidationReason,
    result?: ClientResult<unknown>
  ): void {
    if (authInvalidated) {
      return;
    }

    authInvalidated = true;
    clearAuth();

    if (reason === "tokenExpired") {
      eventEmitter.emit("tokenExpired", result?.error);
      onTokenExpired?.();
    }

    eventEmitter.emit("unauthorized", result?.error);
    eventEmitter.emit("logout");
  }

  // Setup token refresh callback
  tokenManager.setRefreshCallback(async () => {
    const result = await authClient.refreshToken();
    if (result.data) {
      const meResult = await authClient.getMe();
      if (meResult.error) {
        return;
      }
      eventEmitter.emit("tokenRefreshed", result.data.accessToken);
    } else {
      invalidateAuth("tokenExpired", result);
    }
  });

  function handleLoginSuccess(user: User, accessToken: string): void {
    authInvalidated = false;
    storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    storage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    currentUser = user;

    if (autoRefresh) {
      tokenManager.scheduleRefreshFromToken(accessToken, refreshThreshold);
    }

    eventEmitter.emit("login", user);
  }

  function handleUserUpdate(user: User): void {
    currentUser = user;
    storage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }

  function handleLogout(): void {
    eventEmitter.emit("logout");
  }

  function clearAuth(): void {
    storage.clear();
    currentUser = null;
    tokenManager.clearSchedule();
  }

  function getCurrentUser(): User | null {
    return currentUser;
  }

  // Create modules
  const authModule = createAuthModule({
    http: httpClient,
    onLoginSuccess: handleLoginSuccess,
    onLogout: handleLogout,
    autoRedirectAfterLogin,
    storage,
    clearAuth,
    onRefreshFailure: (result) => invalidateAuth("tokenExpired", result),
  });

  const permissionsModule = createPermissionsModule({
    http: httpClient,
    getCurrentUser,
    storage,
  });

  const userModule = createUserModule({
    http: httpClient,
    storage,
    onUserUpdate: handleUserUpdate,
    onAuthFailure: (result) => invalidateAuth("unauthorized", result),
  });

  const oauthModule = createOAuthModule({
    http: httpClient,
    storage,
  });

  const sessionsModule = createSessionsModule({
    http: httpClient,
    storage,
  });

  // Combine all modules
  authClient = {
    ...authModule,
    ...permissionsModule,
    ...userModule,
    ...oauthModule,
    ...sessionsModule,

    // Events
    on(event: AuthEvent, handler: EventHandler): void {
      eventEmitter.on(event, handler);
    },

    off(event: AuthEvent, handler: EventHandler): void {
      eventEmitter.off(event, handler);
    },

    // Utilities
    isAuthenticated(): boolean {
      const token = storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      return !!token;
    },

    getAccessToken(): string | null {
      return storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    },

    setAccessToken(token: string): void {
      authInvalidated = false;
      storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
      if (autoRefresh) {
        tokenManager.scheduleRefreshFromToken(token, refreshThreshold);
      }
    },

    clearAuth,
  };

  // Setup unauthorized handler
  authClient.on("unauthorized", () => {
    onUnauthorized?.();
  });

  // Auto sync user info on initialization
  if (authClient.isAuthenticated()) {
    authClient
      .getMe()
      .then((result) => {
        if (result.error) {
          console.warn("[AuthClient] Failed to sync user info on init:", result.error);
        }
      })
      .catch((error) => {
        console.warn("[AuthClient] Failed to sync user info on init:", error);
      });
  }

  // Auto-handle OAuth callback if enabled (default: true)
  if (autoHandleOAuthCallback && typeof window !== "undefined") {
    const hash = window.location.hash;
    if (hash && (hash.includes("access_token") || hash.includes("accessToken"))) {
      // Automatically process OAuth callback
      authClient.handleOAuthCallback().then((result) => {
        if (result.error) {
          console.error("[AuthClient] Auto OAuth callback failed:", result.error);
        }
      }).catch((error) => {
        console.error("[AuthClient] Auto OAuth callback error:", error);
      });
    }
  }

  return authClient;
}
